import { secondsInMs, msInSeconds } from "lib/temporal/time";
import { dimensionCache } from "./caching/DimensionCache";

export const getVideoSourceWithFallback = ({
  optimalSource,
  fallbackSource,
  callback
}) => {
  // try to get the optimal image
  cacheVideo(optimalSource).then(
    () => {
      // if the optimal image can be cached then use it
      return callback(optimalSource);
    },
    () => {
      // if the optimal image can not be cached then use the original source as a fallback
      return callback(fallbackSource);
    }
  );
};

export const getVideoDurationFromFile = async file => {
  const getVideoDuration = () =>
    new Promise(resolve => {
      let video = document.createElement("video");
      video.preload = "metadata";
      video.onloadedmetadata = () => {
        window.URL.revokeObjectURL(video.src);
        // for the duration to prevent fractional values sent in request
        resolve(Math.floor(video.duration * 1000));
      };
      video.src = window.URL.createObjectURL(file);
    });

  const duration = await getVideoDuration();
  return duration;
};

export const loopTrimmedVideo = (event, elementData) => {
  const { startOffset = 0, trimDuration, duration } = elementData;
  let _trimDuration = trimDuration;
  if (!trimDuration) {
    _trimDuration = duration;
  }
  const endTime = startOffset + _trimDuration;
  const currentTimeInMs = secondsInMs(event.target.currentTime);
  if (currentTimeInMs >= endTime || currentTimeInMs < startOffset) {
    event.target.currentTime = msInSeconds(startOffset);
  }
};

export const getVideoSizeFromSrc = async (src, timeout = 8000) => {
  if (!src) {
    return {
      height: 0,
      width: 0
    };
  }
  const getVideoSize = src => {
    return new Promise((resolve, reject) => {
      let video = document.createElement("video");
      video.preload = "metadata";
      video.onloadedmetadata = () => {
        window.URL.revokeObjectURL(video.src);
        resolve({ height: video.videoHeight, width: video.videoWidth });
      };
      video.onerror = reject;
      video.src = src;
    });
  };

  const timeoutPromise = new Promise((resolve, reject) => {
    setTimeout(() => {
      reject(new Error(`Video dimension loading timed out after ${timeout}ms`));
    }, timeout);
  });

  try {
    return await Promise.race([getVideoSize(src), timeoutPromise]);
  } catch (error) {
    console.warn(`Failed to load video dimensions from ${src}:`, error.message);
    // Return default dimensions on timeout or error
    return { height: 720, width: 1280 };
  }
};

/**
 * Get video size from source with caching support
 * Checks cache first, then falls back to loading if not cached
 */
export const getVideoSizeFromSrcWithCache = async (src, timeout = 8000) => {
  if (!src) {
    return { height: 0, width: 0 };
  }

  // Check cache first
  const cached = dimensionCache.getDimensions(src);
  if (cached) {
    return cached;
  }

  // Load dimensions and cache result
  const dimensions = await getVideoSizeFromSrc(src, timeout);
  dimensionCache.setDimensions(src, dimensions);

  return dimensions;
};

/**
 *
 * @param {Object} videoElement an object representing the video element to get dimensions for
 * @returns {Object} videoDimensions - contains width and height dimensions for an image
 */
export const getVideoDimensionsFromElement = async videoElement => {
  let videoDimensions;
  // check for dimensions in the media object first
  if (
    videoElement.media &&
    videoElement.media.width &&
    videoElement.media.height
  ) {
    videoDimensions = {
      width: videoElement.media.width,
      height: videoElement.media.height
    };
    // Cache media dimensions for future use
    const src =
      videoElement.url || videoElement.originalUrl || videoElement.previewSrc;
    if (src) {
      dimensionCache.setDimensions(src, videoDimensions);
    }
  } else {
    // fallback to using the main url, the original url and then finally the preview url
    // Use cached version for performance
    try {
      videoDimensions = await getVideoSizeFromSrcWithCache(videoElement.url);
    } catch {
      try {
        videoDimensions = await getVideoSizeFromSrcWithCache(
          videoElement.originalUrl
        );
      } catch {
        videoDimensions = await getVideoSizeFromSrcWithCache(
          videoElement.previewSrc
        );
      }
    }
  }
  return videoDimensions;
};

export const getVideoDurationFromSrc = async src => {
  const getVideoDuration = src => {
    return new Promise(resolve => {
      let video = document.createElement("video");
      video.preload = "metadata";
      video.onloadedmetadata = () => {
        window.URL.revokeObjectURL(video.src);
        // floor the duration to prevent fractional values sent in request
        resolve(Math.floor(video.duration * 1000));
      };
      video.src = src;
    });
  };

  return await getVideoDuration(src);
};

export const getVideoDetailsFromSrc = async src => {
  const getVideoDetails = src => {
    return new Promise(resolve => {
      let video = document.createElement("video");
      video.preload = "auto";
      video.onloadeddata = () => {
        window.URL.revokeObjectURL(video.src);
        // floor the duration to prevent fractional values sent in request
        resolve({
          height: video.videoHeight,
          width: video.videoWidth,
          duration: Math.floor(video.duration * 1000)
        });
      };
      video.src = src;
    });
  };

  return await getVideoDetails(src);
};

export const hasAudio = video => {
  return (
    video.mozHasAudio ||
    Boolean(video.webkitAudioDecodedByteCount) ||
    Boolean(video.audioTracks && video.audioTracks.length)
  );
};

export const getVideoHasAudioFromSrc = async (src, timeout = 3000) => {
  // Check cache first
  const cached = dimensionCache.getHasAudio(src);
  if (cached !== null) {
    return cached;
  }

  const getVideoHasAudio = src => {
    return new Promise((resolve, reject) => {
      let video = document.createElement("video");
      video.preload = "auto";
      video.onloadeddata = () => {
        window.URL.revokeObjectURL(video.src);
        resolve(hasAudio(video));
      };
      video.onerror = () => {
        reject(new Error("Failed to load video for audio detection"));
      };
      video.src = src;
    });
  };

  const timeoutPromise = new Promise((resolve, reject) => {
    setTimeout(() => {
      reject(new Error(`Video audio detection timed out after ${timeout}ms`));
    }, timeout);
  });

  try {
    const result = await Promise.race([getVideoHasAudio(src), timeoutPromise]);
    // Cache the result
    dimensionCache.setHasAudio(src, result);
    return result;
  } catch (error) {
    console.warn(`Failed to detect audio for ${src}:`, error.message);
    // Default to false on error
    dimensionCache.setHasAudio(src, false);
    return false;
  }
};

export const cacheVideo = src => {
  return new Promise((resolve, reject) => {
    const video = document.createElement("video");

    video.onloadeddata = () => {
      resolve(true);
    };

    video.onerror = () => {
      reject(false);
    };

    video.src = src;
  });
};
